import {Injectable} from '@angular/core';
import {CreatePack, Pack, PackModel} from '../models/pack.model';
import {environment} from '../../environments/environment';
import {Observable, of, share, switchMap} from 'rxjs';
import {PromptModel} from '../models/prompt.model';
import {PackItemModel} from '../models/pack-item.model';
import {OrganizationEntityApiService} from './organization-entity-api.service';
import {PackVersionModel} from '../models/pack-version.model';

/**
 * Service for managing packs within organizations.
 * All operations are scoped to organizations and default to the active organization.
 */
@Injectable({
  providedIn: 'root'
})
export class PackService extends OrganizationEntityApiService<Pack, PackModel, CreatePack> {
  constructor() {
    // Packs are scoped to organizations
    // Routes: /organizations/:orgId/packs
    super(PackModel, 'packs', 'organizations');
  }

  /**
   * Render a pack or prompt for preview
   * @param packVersion - Pack version to render
   */
  render(packVersion: PackVersionModel): Observable<string> {
    return this.http.get(
      environment.apiBaseUrl + `/p/${packVersion.full_name}`,
      {responseType: 'text'}
    ).pipe(share())
  }

  /**
   * Update pack items by creating a new version. Defaults to active organization if no orgId provided.
   * @param orgId - Organization ID
   * @param packId - Pack ID
   * @param items - Items array (only when orgId is provided)
   */
  updateItems(packId: string, items: readonly PackItemModel[]): Observable<PackModel> {
    return this.withOrgId(
      (orgId: string) => {
        return this.post<Pack>(
          `organizations/${orgId}/packs/${packId}/versions`,
          {
            items: items.map(p => {
              if (p instanceof PromptModel) {
                return {prompt_id: p.id, version: p.version}
              } else {
                return {pack_id: p.id, version: p.version}
              }
            })
          }
        )
      }
    )
  }
}
